import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import useMixedState from '@shared/hooks/mixed-state';
import { useComponentDidMount } from '@shared/hooks/react';
import type { LocalStorage } from '@shared/storage/local';

const NoOp = (): void => {};

const DarkSchemeQuery = '(prefers-color-scheme: dark)';

const systemColorScheme = (): ResolvedApplicationColorScheme =>
	window.matchMedia(DarkSchemeQuery).matches ? 'dark' : 'light';

export type ApplicationColorScheme = 'dark' | 'light' | 'system';

export type ResolvedApplicationColorScheme = Exclude<
	ApplicationColorScheme,
	'system'
>;

export interface ApplicationState {
	colorScheme: ApplicationColorScheme;
	setColorScheme: (colorScheme: ApplicationColorScheme) => void;

	/**
	 *	The club or organization brand the interface is painted in, or `null`
	 *	for whatever the stylesheet ships. Every brand token derives from it.
	 */
	brandColor: string | null;
	setBrandColor: (brandColor: string | null) => void;

	isProcessing: boolean;
	setProcessing: (isProcessing: boolean) => void;

	isLoading: boolean;
	setLoading: (isLoading: boolean) => void;
}

const DefaultApplicationState: ApplicationState = {
	colorScheme: 'system',
	setColorScheme: NoOp,

	brandColor: null,
	setBrandColor: NoOp,

	isProcessing: false,
	setProcessing: NoOp,

	isLoading: false,
	setLoading: NoOp,
};

const ApplicationStateContext = createContext<ApplicationState>(
	DefaultApplicationState
);

export function useApplicationState(): ApplicationState {
	return useContext(ApplicationStateContext);
}

interface ApplicationStateProviderProps {
	storage: LocalStorage;

	children: ReactNode | ReactNode[];
}

export function ApplicationStateProvider({
	storage,
	children,
}: ApplicationStateProviderProps): ReactNode {
	const [state, setState] = useMixedState<ApplicationState>(
		DefaultApplicationState
	);

	useComponentDidMount(() => {
		const colorScheme = storage.get<ApplicationColorScheme>(
			'colorScheme',
			'system'
		);

		setColorScheme(colorScheme);
		setBrandColor(storage.get<string>('brandColor', '') || null);

		const followSystem = (): void => {
			if (
				storage.get<ApplicationColorScheme>('colorScheme', 'system') ===
				'system'
			) {
				pinColorScheme(systemColorScheme());
			}
		};

		const query = window.matchMedia(DarkSchemeQuery);
		query.addEventListener('change', followSystem);

		return () => query.removeEventListener('change', followSystem);
	});

	/**
	 *	The root always carries the scheme in use, the system's included, so
	 *	a stylesheet can read it back where `light-dark()` cannot be asked:
	 *	a surface that flips the scheme under itself matches on this style.
	 */
	const pinColorScheme = (
		colorScheme: ResolvedApplicationColorScheme
	): void => {
		document.documentElement.style.colorScheme = colorScheme;
	};

	const setColorScheme = (colorScheme: ApplicationColorScheme) => {
		setState({ colorScheme });

		storage.set('colorScheme', colorScheme);

		pinColorScheme(
			colorScheme === 'system' ? systemColorScheme() : colorScheme
		);
	};

	const setBrandColor = (brandColor: string | null) => {
		setState({ brandColor });

		storage.set('brandColor', brandColor ?? '');

		if (brandColor) {
			document.documentElement.style.setProperty(
				'--color-raw-brand',
				brandColor
			);
		} else {
			document.documentElement.style.removeProperty('--color-raw-brand');
		}
	};

	const setProcessing = (isProcessing: boolean) => {
		setState({ isProcessing });
	};

	const setLoading = (isLoading: boolean) => {
		setState({ isLoading });
	};

	const providedState: ApplicationState = {
		...state,

		setColorScheme,
		setBrandColor,

		setProcessing,
		setLoading,
	};

	return (
		<ApplicationStateContext.Provider value={providedState}>
			{children}
		</ApplicationStateContext.Provider>
	);
}
